import { COBarChartViewProps } from 'enums/COBarChartEums';
import React, { useRef, useState } from 'react';
import styles from './COBarChart.module.scss';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Label,
  XAxisProps,
} from 'recharts';
import LoaderAnimation from '../LoaderAnimation';
import GraphDropDown, {
  GraphDropDownProps,
} from 'views/Dashboard/COBarChart/GraphDropdown';
import { BreakDownDropdownOptionsEnums } from 'enums/BreakDownDropdownOptions';
import { Spin } from 'antd';
import { DownloadLoader } from 'enums/GraphDropdownEnums';
import { renderTooltipContent } from 'views/Dashboard/COBarChart';
import { CategoryOptions } from 'views/Dashboard';

export interface COBarChartProductProps {
  data?: CategoryOptions[];
  loading: boolean;
  graphDropDownProps?: GraphDropDownProps;
  yAxisLabel?: string;
  isPercentage?: boolean;
  isBreakdown?: boolean;
  isSort?: boolean;
  isDownload?: boolean;
  selectedOption?: string;
  graphName?: string;
}

const COBarChartProduct: React.FC<COBarChartProductProps> = ({
  data,
  loading,
  graphDropDownProps,
  yAxisLabel = BreakDownDropdownOptionsEnums.TOTAL,
  isPercentage = true,
  isBreakdown,
  isSort,
  isDownload,
  selectedOption,
  graphName,
}: COBarChartProductProps) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  function CustomizedTick(props: XAxisProps & { payload: { value: string } }) {
    const { x, y, payload } = props;
    const words = payload.value.split(' ');
    const fontSize = 12;
    const lineHeight = 16;
    const maxCharsPerLine = 8;
    const lines = [];
    let line = words[0];
    for (let i = 1; i < words.length; i++) {
      if (line.length + words[i].length + 1 <= maxCharsPerLine) {
        line += ' ' + words[i];
      } else {
        lines.push(line);
        line = words[i];
      }
    }
    lines.push(line);
    const totalHeight = lines.length * lineHeight;
    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, index) => (
          <text
            key={index}
            x={0}
            y={index * lineHeight - totalHeight / 2 + lineHeight / 2}
            dy={10}
            dx={-37}
            transform={`rotate(-45)`}
            fill="#666"
            fontSize={fontSize}
            fontWeight="900"
            textAnchor="middle">
            {line}
          </text>
        ))}
      </g>
    );
  }

  if (loading)
    return (
      <div className={styles.loaderContainer}>
        <LoaderAnimation />
      </div>
    );
  const customMargin = {
    top: 40,
    right: 29,
    bottom: 70,
    left: 40,
  };
  const customPadding = {
    left: data?.length === 1 ? 200 : 0,
    right: data?.length === 1 ? 400 : 0,
  };

  const YAxisLabel = isBreakdown
    ? yAxisLabel === BreakDownDropdownOptionsEnums.TOTAL
      ? COBarChartViewProps.PRODUCT_LABEL_TOTAL
      : yAxisLabel === BreakDownDropdownOptionsEnums.STAGE
      ? COBarChartViewProps.PRODUCT_LABEL_STAGE
      : COBarChartViewProps.LABEL_VALUE
    : isPercentage
    ? COBarChartViewProps.PRODUCT_LABEL_TOTAL
    : COBarChartViewProps.LABEL_VALUE;
  return (
    <Spin spinning={!!isDownloading} tip={DownloadLoader.DOWNLOADING}>
      {graphDropDownProps && (
        <GraphDropDown
          handleDownload={graphDropDownProps.handleDownload}
          handleSort={graphDropDownProps.handleSort}
          graphRef={graphRef}
          downloadCsv={graphDropDownProps.downloadCsv}
          isSort={isSort}
          isDownload={isDownload}
          selectedOption={selectedOption}
          downloadFileName={graphName}
          setIsDownloading={setIsDownloading}
        />
      )}
      <div ref={graphRef}>
        <ResponsiveContainer width={COBarChartViewProps.RES_WIDTH} height={400}>
          <BarChart data={data} margin={customMargin}>
            <CartesianGrid
              strokeDasharray="5 3"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              strokeDasharray="5 5"
              angle={-45}
              dy={42}
              dx={-45}
              className={styles.xAxisLabel}
              tick={props => <CustomizedTick {...props} />}
              padding={customPadding}
            />
            <YAxis
              domain={
                isBreakdown
                  ? yAxisLabel !== BreakDownDropdownOptionsEnums.KG
                    ? [0, 100]
                    : [0, 0]
                  : isPercentage
                  ? [0, 100]
                  : [0, 0]
              }
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}>
              <Label
                value={YAxisLabel}
                position={COBarChartViewProps.LABEL_POSITION}
                angle={-90}
                offset={-10}
                style={{
                  textAnchor: COBarChartViewProps.TEXT_ANCHOR,
                  fill: COBarChartViewProps.TEXT_ANCHOR_FILL,
                }}
                className={styles.labels}
              />
            </YAxis>
            <Tooltip
              {...(YAxisLabel === COBarChartViewProps.LABEL_VALUE && {
                content: renderTooltipContent,
              })}
            />
            <Bar
              dataKey={COBarChartViewProps.BAR_DATAKEY}
              fill={COBarChartViewProps.BAR_FILL}
              radius={[5, 5, 0, 0]}
              barSize={35}>
              <LabelList
                dataKey={COBarChartViewProps.LABEL_LIST_DATAKEY}
                position={COBarChartViewProps.LABEL_LIST_POSITION}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Spin>
  );
};

export default COBarChartProduct;
